/**
 * EnterpriseSpreadsheet.jsx
 * Root orchestrator for the enterprise spreadsheet platform.
 *
 * Responsibilities:
 *  - Load workbook metadata (from API or prop)
 *  - Initialize HyperFormula via formulaWorkerBridge
 *  - Coordinate toolbar, formula bar, sheet tabs, grid, status bar
 *  - Dispatch save / submit / export / add-row actions
 *  - Wire Zustand stores together
 *
 * Usage:
 *   <EnterpriseSpreadsheet workbookId="wb_revenue_2025" />
 *
 * Or with inline metadata (for dev/testing):
 *   <EnterpriseSpreadsheet metadata={WORKBOOK_METADATA} initialData={DATA_MAP} />
 */

import React, {
  useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy,
} from 'react';
import axios from 'axios';

// ── Components ────────────────────────────────────────────────────────────────
import SpreadsheetToolbar    from './SpreadsheetToolbar';
import FormulaBar            from './FormulaBar';
import WorkbookTabs          from './WorkbookTabs';
import SpreadsheetStatusBar  from './SpreadsheetStatusBar';
import GridWrapper           from './GridWrapper';

// ── Libraries ─────────────────────────────────────────────────────────────────
import formulaWorkerBridge   from '../../../libraries/spreadsheet/workers/formulaWorkerBridge';
import metadataEngine        from '../../../libraries/spreadsheet/core/metadata/metadataEngine';
import validationEngine      from '../../../libraries/spreadsheet/core/validation/validationEngine';
import { parseColumnsToAgDefs, buildHfSheetsData } from '../../../libraries/spreadsheet/core/metadata/metadataParser';
import { workbookApi, metadataApi }                from '../../../libraries/spreadsheet/services/workbookApi';
import { parseCellAddress, fieldToColIndex }        from '../../../libraries/spreadsheet/utils/cellAddressParser';

// ── Stores ────────────────────────────────────────────────────────────────────
import {
  useWorkbookStore, useSheetStore, useFormulaStore,
  useMetadataStore, useUiStore,
} from '../../../libraries/spreadsheet/store/spreadsheetStores';

// ── Your project's existing services ─────────────────────────────────────────
import { CreateData }        from '../../../services/process/process_data/process_data';
import { Fetchupdatequery }  from '../../../services/process/BRS1/brsService7';

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE METADATA  (replace with your API fetch in production)
// ─────────────────────────────────────────────────────────────────────────────
const SAMPLE_METADATA = {
  workbookId:   'wb_revenue_2025',
  workbookName: 'FY 2025 Revenue Planning',
  fiscalYear:   '2025',
  lastSaved:    '2025-05-20 14:32',
  sheets: [
    {
      sheetId:   'sh_revenue',
      sheetName: 'Revenue Planning',
      database:  'finance',
      table:     'revenue_planning',
      permissions: { canEdit: true, canSave: true, canSubmit: true, canApprove: false, canExport: true, canAddRow: true, canDeleteRow: false },
      toolbarActions: [
        { id: 'save',    label: 'Save',    icon: 'save',     type: 'primary',   permission: 'canSave'   },
        { id: 'submit',  label: 'Submit',  icon: 'submit',   type: 'secondary', permission: 'canSubmit' },
        { id: 'export',  label: 'Export',  icon: 'download', type: 'ghost',     permission: 'canExport' },
        { id: 'addRow',  label: 'Add Row', icon: 'addRow',   type: 'ghost',     permission: 'canAddRow' },
        { id: 'refresh', label: 'Refresh', icon: 'refresh',  type: 'ghost',     permission: null        },
      ],
      columns: [
        { field: 'account',     headerName: 'Account',     datatype: 'string',   editable: false, width: 200, pinned: 'left' },
        { field: 'department',  headerName: 'Department',  datatype: 'string',   editable: true, width: 150, pinned: 'left' },
        { field: 'jan',         headerName: 'Jan',         datatype: 'currency', editable: true,  width: 130, validation: { required: true, min: 0 } },
        { field: 'feb',         headerName: 'Feb',         datatype: 'currency', editable: true,  width: 130, validation: { required: true, min: 0 } },
        { field: 'mar',         headerName: 'Mar',         datatype: 'currency', editable: true,  width: 130, validation: { required: true, min: 0 } },
        { field: 'apr',         headerName: 'Apr',         datatype: 'currency', editable: true,  width: 130 },
        { field: 'may',         headerName: 'May',         datatype: 'currency', editable: true,  width: 130 },
        { field: 'jun',         headerName: 'Jun',         datatype: 'currency', editable: true,  width: 130 },
        { field: 'q1_total',    headerName: 'Q1 Total',    datatype: 'currency', editable: false, width: 150, formula: '=SUM(C{ROW}:E{ROW})', cellStyle: { background: '#eef2ff', fontWeight: 700, color: '#3730a3' } },
        { field: 'q2_total',    headerName: 'Q2 Total',    datatype: 'currency', editable: false, width: 150, formula: '=SUM(F{ROW}:H{ROW})', cellStyle: { background: '#eef2ff', fontWeight: 700, color: '#3730a3' } },
        { field: 'h1_total',    headerName: 'H1 Total',    datatype: 'currency', editable: false, width: 160, formula: '=I{ROW}+J{ROW}',      cellStyle: { background: '#dbeafe', fontWeight: 700, color: '#1e40af' } },
        { field: 'variance_pct',headerName: 'Var %',       datatype: 'percent',  editable: false, width: 100, conditionalFormatting: { negative: { color: '#dc2626' }, positive: { color: '#16a34a' } } },
        { field: 'status',      headerName: 'Status',      datatype: 'badge',    editable: false, width: 120,
          badgeConfig: {
            Draft:     { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
            Submitted: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
            Approved:  { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
            Locked:    { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
          },
        },
      ],
    },
    {
      sheetId:   'sh_expenses',
      sheetName: 'Expense Planning',
      database:  'finance',
      table:     'expense_planning',
      permissions: { canEdit: true, canSave: true, canSubmit: false, canApprove: false, canExport: true, canAddRow: false, canDeleteRow: false },
      toolbarActions: [
        { id: 'save',    label: 'Save',    icon: 'save',     type: 'primary', permission: 'canSave'   },
        { id: 'export',  label: 'Export',  icon: 'download', type: 'ghost',   permission: 'canExport' },
        { id: 'refresh', label: 'Refresh', icon: 'refresh',  type: 'ghost',   permission: null        },
      ],
      columns: [
        { field: 'expense_category', headerName: 'Category',     datatype: 'string',   editable: false, width: 200, pinned: 'left' },
        { field: 'cost_center',      headerName: 'Cost Center',  datatype: 'string',   editable: false, width: 150, pinned: 'left' },
        { field: 'q1_budget',        headerName: 'Q1 Budget',    datatype: 'currency', editable: true,  width: 150, validation: { min: 0 } },
        { field: 'q2_budget',        headerName: 'Q2 Budget',    datatype: 'currency', editable: true,  width: 150, validation: { min: 0 } },
        { field: 'q1_actual',        headerName: 'Q1 Actual',    datatype: 'currency', editable: false, width: 150, cellStyle: { background: '#f8fafc' } },
        { field: 'total_budget',     headerName: 'Total Budget', datatype: 'currency', editable: false, width: 160, formula: '=C{ROW}+D{ROW}', cellStyle: { background: '#eef2ff', fontWeight: 700, color: '#3730a3' } },
        { field: 'utilization_pct',  headerName: 'Utilization %',datatype: 'percent',  editable: false, width: 130,
          conditionalFormatting: {
            thresholds: [
              { operator: '>=', value: 90, background: '#fef2f2', color: '#dc2626' },
              { operator: '>=', value: 70, background: '#fffbeb', color: '#d97706' },
            ],
          },
        },
        { field: 'status', headerName: 'Status', datatype: 'badge', editable: false, width: 120,
          badgeConfig: {
            Draft:     { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
            Approved:  { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
            Locked:    { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
          },
        },
      ],
    },
  ],
};

const SAMPLE_DATA = {
  sh_revenue: [
    { id: 1, account: 'Product Revenue',     department: 'Sales',     jan: 450000, feb: 420000, mar: 510000, apr: 490000, may: 530000, jun: 560000, q1_total: 1380000, q2_total: 1580000, h1_total: 2960000, variance_pct: 9.6,  status: 'Approved'  },
    { id: 2, account: 'Service Revenue',     department: 'Services',  jan: 120000, feb: 135000, mar: 118000, apr: 140000, may: 155000, jun: 148000, q1_total: 373000,  q2_total: 443000,  h1_total: 816000,  variance_pct: 16.6, status: 'Submitted' },
    { id: 3, account: 'Subscription Rev',   department: 'Digital',   jan:  80000, feb:  82000, mar:  85000, apr:  88000, may:  91000, jun:  94000, q1_total: 247000,  q2_total: 273000,  h1_total: 520000,  variance_pct: 8.3,  status: 'Draft'     },
    { id: 4, account: 'Consulting Revenue', department: 'Advisory',  jan:  60000, feb:  72000, mar:  55000, apr:  68000, may:  74000, jun:  81000, q1_total: 187000,  q2_total: 223000,  h1_total: 410000,  variance_pct: 7.9,  status: 'Draft'     },
    { id: 5, account: 'License Revenue',    department: 'Product',   jan: 200000, feb: 195000, mar: 210000, apr: 205000, may: 215000, jun: 220000, q1_total: 605000,  q2_total: 640000,  h1_total: 1245000, variance_pct: 3.8,  status: 'Locked'    },
    { id: 6, account: 'Partner Revenue',    department: 'Alliances', jan:  35000, feb:  38000, mar:  41000, apr:  44000, may:  47000, jun:  50000, q1_total: 114000,  q2_total: 141000,  h1_total: 255000,  variance_pct: 6.25, status: 'Draft'     },
  ],
  sh_expenses: [
    { id: 1, expense_category: 'Salaries & Wages',     cost_center: 'HR',          q1_budget: 900000, q2_budget: 920000, q1_actual: 875000, total_budget: 1820000, utilization_pct: 48.1, status: 'Approved' },
    { id: 2, expense_category: 'Marketing Spend',      cost_center: 'Marketing',   q1_budget: 180000, q2_budget: 200000, q1_actual: 195000, total_budget: 380000,  utilization_pct: 51.3, status: 'Draft'    },
    { id: 3, expense_category: 'Cloud Infrastructure', cost_center: 'Engineering', q1_budget: 120000, q2_budget: 130000, q1_actual:  98000, total_budget: 250000,  utilization_pct: 39.2, status: 'Draft'    },
    { id: 4, expense_category: 'Travel & Entertainment',cost_center:'Sales',       q1_budget:  45000, q2_budget:  50000, q1_actual:  51000, total_budget:  95000,  utilization_pct: 53.7, status: 'Locked'   },
    { id: 5, expense_category: 'R&D Expenses',         cost_center: 'Product',     q1_budget: 250000, q2_budget: 260000, q1_actual: 231000, total_budget: 510000,  utilization_pct: 45.3, status: 'Approved' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function EnterpriseSpreadsheet({
  workbookId,
  metadata: propMetadata,
  initialData: propData,
}) {
  const [userCredentials] = useState(() => JSON.parse(sessionStorage.getItem('userCredentials') || '{}'));
  const [isLoading,    setIsLoading]    = useState(true);
  const [metadata,     setMetadata]     = useState(null);
  const [hfReady,      setHfReady]      = useState(false);
  const gridWrapperRef = useRef(null);

  // ── Stores ────────────────────────────────────────────────────────────────
  const { setWorkbook, activeSheetId, setActiveSheet, setSaveStatus, setLastSaved, isSaving } = useWorkbookStore();
  const { rowDataMap, setSheetData, updateCell, getDirtyRows, getDirtyCount, clearDirty, addRow: storeAddRow, getTotalErrorCount } = useSheetStore();
  const { addToast, searchText } = useUiStore();
  const { setFormulaBarValue, activeCellAddress } = useFormulaStore();
  const resolvedMetadata = propMetadata || (workbookId ? null : SAMPLE_METADATA);
  const resolvedDataMap = propData || SAMPLE_DATA;

  // ─────────────────────────────────────────────────────────────────────────
  // 1. LOAD METADATA
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        let meta;
        if (resolvedMetadata) {
          meta = resolvedMetadata;
        } else if (workbookId) {
          meta = await metadataApi.fetchWorkbookMetadata(workbookId);
        } else {
          meta = SAMPLE_METADATA; // dev fallback
        }

        // Register with metadata engine
        const enriched = metadataEngine.registerWorkbook(meta.workbookId, meta);
        setMetadata(enriched);
        setWorkbook(enriched);

        // Load row data for all sheets
        enriched.sheets.forEach((sheet) => {
          const rows = resolvedDataMap[sheet.sheetId] || [];
          setSheetData(sheet.sheetId, rows);
        });

        // Initialize HyperFormula worker
        await initHyperFormula(enriched, resolvedDataMap);
        setHfReady(true);
      } catch (err) {
        console.error('[EnterpriseSpreadsheet] Load error:', err);
        addToast('Failed to load workbook: ' + err.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };
    load();

    return () => formulaWorkerBridge.terminate();
  }, [workbookId, resolvedMetadata, resolvedDataMap, addToast, setWorkbook, setSheetData]);

  // ─────────────────────────────────────────────────────────────────────────
  // 2. INIT HYPERFORMULA
  // ─────────────────────────────────────────────────────────────────────────
  const initHyperFormula = async (meta, dataMap) => {
    const sheetsMeta = meta.sheets.map((s) => ({
      sheetId:   s.sheetId,
      sheetName: s.sheetName,
      columns:   s.columns,
    }));

    await formulaWorkerBridge.init(sheetsMeta, dataMap);

    // Register HF sheet indices
    meta.sheets.forEach((sheet, idx) => {
      metadataEngine.registerHfSheetIndex(sheet.sheetId, idx);
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Derived active sheet values
  // ─────────────────────────────────────────────────────────────────────────
  const activeSheet = useMemo(() => {
    if (!metadata || !activeSheetId) return null;
    return metadata.sheets.find((s) => s.sheetId === activeSheetId) || metadata.sheets[0];
  }, [metadata, activeSheetId]);

  const activeSheetIdResolved = activeSheet?.sheetId;
  const hfSheetIndex = activeSheetIdResolved
    ? metadataEngine.getHfSheetIndex(activeSheetIdResolved)
    : 0;

  const currentRows = useMemo(
    () => rowDataMap[activeSheetIdResolved] || [],
    [rowDataMap, activeSheetIdResolved]
  );

  const agColumnDefs = useMemo(() => {
    if (!activeSheet || !metadata) return [];
    return metadataEngine.getAgColumnDefs(
      metadata.workbookId,
      activeSheet.sheetId,
      activeSheet.effectivePermissions
    );
  }, [activeSheet, metadata]);

  const permissions   = activeSheet?.effectivePermissions || {};
  const toolbarActions = activeSheet?.toolbarActions || [];
  const dirtyCount     = getDirtyCount(activeSheetIdResolved);
  const errorCount     = getTotalErrorCount(activeSheetIdResolved);

  // ─────────────────────────────────────────────────────────────────────────
  // Compute active cell editability
  // ─────────────────────────────────────────────────────────────────────────
  const isActiveCellEditable = useMemo(() => {
    if (!activeSheet || !activeCellAddress) return false;
    
    // Parse cell address (e.g., "A1" -> col: 0, row: 0)
    const parts = activeCellAddress.match(/^([A-Z]+)(\d+)$/i);
    if (!parts) return false;
    
    const colLetter = parts[1].toUpperCase();
    const rowNum = parseInt(parts[2], 10) - 1; // Convert to 0-based
    
    // Convert column letter to index
    let colIdx = 0;
    for (let i = 0; i < colLetter.length; i++) {
      colIdx = colIdx * 26 + (colLetter.charCodeAt(i) - 'A'.charCodeAt(0) + 1);
    }
    colIdx -= 1; // Convert to 0-based
    
    const col = activeSheet.columns[colIdx];
    if (!col) return false;
    
    return col.editable !== false && !col.formula && permissions.canEdit !== false;
  }, [activeSheet, activeCellAddress, permissions.canEdit]);

  // ─────────────────────────────────────────────────────────────────────────
  // 3. SAVE (uses your project's CreateData + Fetchupdatequery)
  // ─────────────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!activeSheet) return;
    const sid  = activeSheet.sheetId;
    const rows = getDirtyRows(sid);
    if (!rows.length) { addToast('Nothing to save', 'info'); return; }

    setSaveStatus('saving');
    try {
      const existingIds = new Set((SAMPLE_DATA[sid] || []).map((r) => r.id));

      for (const row of rows) {
        const { id, ...fields } = row;
        const isNew = !existingIds.has(id);

        if (isNew) {
          // ── INSERT via your CreateData service ──────────────────────────
          await CreateData(
            activeSheet.database,
            activeSheet.table,
            {
              ...fields,
              created_by: userCredentials?.username,
              entity_id:  userCredentials?.selectedentity,
              tenant_id:  userCredentials?.tenants_id,
            }
          );
        } else {
          // ── UPDATE via your Fetchupdatequery service ────────────────────
          const [headers, url] = Fetchupdatequery();
          await axios.post(url, {
            schema_name: activeSheet.database,
            table:       activeSheet.table,
            set_column:  { ...fields, updated_by: userCredentials?.username },
            where:       [{ id }],
          }, headers);
        }
      }

      clearDirty(sid);
      const now = new Date().toLocaleString('en-IN');
      setLastSaved(now);
      addToast(`${rows.length} row(s) saved successfully`, 'success');
    } catch (err) {
      console.error('[EnterpriseSpreadsheet] Save error:', err);
      setSaveStatus('error');
      addToast('Save failed: ' + err.message, 'error');
    }
  }, [activeSheet, getDirtyRows, setSaveStatus, clearDirty, setLastSaved, addToast, userCredentials]);

  // ─────────────────────────────────────────────────────────────────────────
  // 4. SUBMIT
  // ─────────────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (errorCount > 0) { addToast('Fix validation errors before submitting', 'error'); return; }
    if (dirtyCount > 0) { addToast('Save before submitting', 'error'); return; }

    setSaveStatus('saving');
    try {
      await workbookApi.submitSheet(metadata.workbookId, activeSheet.sheetId);
      addToast('Sheet submitted for approval', 'success');
      setSaveStatus('saved');
    } catch (err) {
      addToast('Submit failed: ' + err.message, 'error');
      setSaveStatus('unsaved');
    }
  }, [activeSheet, metadata, errorCount, dirtyCount, addToast, setSaveStatus]);

  // ─────────────────────────────────────────────────────────────────────────
  // 5. EXPORT
  // ─────────────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    gridWrapperRef.current?.exportCsv({
      fileName: `${activeSheet?.sheetName}_${new Date().toISOString().slice(0, 10)}.csv`,
    });
    addToast('Export started', 'info');
  }, [activeSheet, addToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // 6. ADD ROW
  // ─────────────────────────────────────────────────────────────────────────
  const handleAddRow = useCallback(async () => {
    if (!activeSheet) return;

    const sid = activeSheet.sheetId;
    const rows = rowDataMap[sid] || [];
    const newId = Math.max(0, ...rows.map((r) => r.id || 0)) + 1;
    const newRow = { id: newId, status: 'Draft' };

    storeAddRow(sid, newRow);

    try {
      if (!hfReady) return;

      const hfIdx = metadataEngine.getHfSheetIndex(sid);
      const rowIndex = rows.length;

      await formulaWorkerBridge.setCells(hfIdx, activeSheet.columns.map((col, colIdx) => ({
        row: rowIndex,
        col: colIdx,
        value: col.formula ?? null,
      })));
    } catch (err) {
      console.error('[EnterpriseSpreadsheet] HyperFormula row seed error:', err);
    }

    addToast('New row added', 'info');
  }, [activeSheet, hfReady, rowDataMap, storeAddRow, addToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // 7. TOOLBAR ACTION DISPATCHER
  // ─────────────────────────────────────────────────────────────────────────
  const handleAction = useCallback((actionId) => {
    const handlers = {
      save:    handleSave,
      submit:  handleSubmit,
      export:  handleExport,
      addRow:  handleAddRow,
      refresh: () => { addToast('Refreshing…', 'info'); },
    };
    handlers[actionId]?.();
  }, [handleSave, handleSubmit, handleExport, handleAddRow, addToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // 8. FORMULA BAR COMMIT / CANCEL
  // ─────────────────────────────────────────────────────────────────────────
  const handleFormulaCommit = useCallback(async (address, formula) => {
    const parsed = parseCellAddress(address);
    if (!parsed || !activeSheet) return;

    const col = activeSheet.columns[parsed.col];
    if (!col || !col.editable || col.formula) {
      addToast('This cell is not editable', 'info');
      return;
    }

    // Push value into the grid cell via GridWrapper ref (handles open editor too)
    gridWrapperRef.current?.commitFormulaBarValue?.(formula);

    // Also update store + HF directly as a safety net
    const row = currentRows[parsed.row];
    if (row) {
      updateCell(activeSheet.sheetId, row.id, col.field, formula);
      try {
        await formulaWorkerBridge.setCell(hfSheetIndex, parsed.row, parsed.col, formula);
      } catch (err) {
        console.error('[EnterpriseSpreadsheet] Formula commit error:', err);
      }
    }
  }, [activeSheet, currentRows, updateCell, hfSheetIndex, addToast]);

  const handleFormulaCancel = useCallback(() => {
    gridWrapperRef.current?.getApi()?.stopEditing(true);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // 9. KEYBOARD SHORTCUTS
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleSave]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (isLoading) return <LoadingScreen />;

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div style={{
        display: 'flex', flexDirection: 'column',
        height: '100vh', background: '#f1f5f9',
        fontFamily: "'DM Sans', sans-serif",
      }}>

        {/* ── App Header ─────────────────────────────────────────────────── */}
        <AppHeader metadata={metadata} />

        {/* ── Toolbar ────────────────────────────────────────────────────── */}
        <SpreadsheetToolbar
          workbookName={metadata?.workbookName}
          sheetName={activeSheet?.sheetName}
          toolbarActions={toolbarActions}
          permissions={permissions}
          onAction={handleAction}
          errorCount={errorCount}
          dirtyCount={dirtyCount}
        />

        {/* ── Formula Bar ────────────────────────────────────────────────── */}
        <FormulaBar
          onFormulaCommit={handleFormulaCommit}
          onFormulaCancel={handleFormulaCancel}
          activeSheetIndex={hfSheetIndex}
          columns={activeSheet?.columns || []}
          isActiveCellEditable={isActiveCellEditable}
        />

        {/* ── Sheet Tabs ─────────────────────────────────────────────────── */}
        <WorkbookTabs
          sheets={metadata?.sheets || []}
          activeSheetId={activeSheetIdResolved}
          onSelectSheet={setActiveSheet}
          canAddSheet={false}
        />

        {/* ── Grid ───────────────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {activeSheet && (
            <GridWrapper
              ref={gridWrapperRef}
              key={activeSheetIdResolved}
              columnDefs={agColumnDefs}
              rowData={currentRows}
              sheetMeta={activeSheet}
              permissions={permissions}
              hfSheetIndex={hfSheetIndex}
              workbookId={metadata?.workbookId}
              onCellChanged={() => useWorkbookStore.getState().markUnsaved()}
            />
          )}

          {/* HyperFormula not ready overlay */}
          {!hfReady && currentRows.length === 0 && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 50,
            }}>
              <div style={{ color: '#1a56db', fontSize: 13 }}>Initializing formula engine…</div>
            </div>
          )}
        </div>

        {/* ── Status Bar ─────────────────────────────────────────────────── */}
        <SpreadsheetStatusBar
          rowCount={currentRows.length}
          filteredRowCount={currentRows.length}
          selectedRowCount={0}
          dirtyCount={dirtyCount}
          errorCount={errorCount}
          canEdit={permissions.canEdit}
          activeSheetName={activeSheet?.sheetName}
        />

      </div>

      {/* ── Toast Notifications ─────────────────────────────────────────── */}
      <ToastContainer />
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const AppHeader = ({ metadata }) => (
  <div style={{
    background: 'linear-gradient(135deg, #1e3a5f 0%, #1a56db 100%)',
    padding: '0 18px', height: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexShrink: 0,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
      </svg>
      <span style={{ color: '#bfdbfe', fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
        Enterprise Planning Console
      </span>
      <span style={{ color: '#3b82f6' }}>›</span>
      <span style={{ color: '#93c5fd', fontSize: 11 }}>{metadata?.workbookName}</span>
      {metadata?.fiscalYear && (
        <>
          <span style={{ color: '#3b82f6' }}>›</span>
          <span style={{ color: '#bfdbfe', fontSize: 11 }}>FY {metadata.fiscalYear}</span>
        </>
      )}
    </div>
    {metadata?.lastSaved && (
      <span style={{ color: '#6b8cce', fontSize: 10 }}>Last saved: {metadata.lastSaved}</span>
    )}
  </div>
);

const LoadingScreen = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f1f5f9', flexDirection: 'column', gap: 12 }}>
    <div style={{ width: 32, height: 32, border: '3px solid #bfdbfe', borderTopColor: '#1a56db', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <span style={{ color: '#4b5563', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>Loading workbook…</span>
  </div>
);

// Toast container reads from UiStore
function ToastContainer() {
  const { toasts, removeToast } = useUiStore();

  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map((t) => {
        const colors = {
          success: { bg: '#f0fdf4', border: '#16a34a', icon: '✓', color: '#16a34a' },
          error:   { bg: '#fef2f2', border: '#dc2626', icon: '✕', color: '#dc2626' },
          info:    { bg: '#eff6ff', border: '#3b82f6', icon: 'i', color: '#3b82f6' },
        }[t.type] || { bg: '#f9fafb', border: '#9ca3af', icon: '·', color: '#6b7280' };

        return (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: 8, minWidth: 280, maxWidth: 400,
            background: colors.bg, borderLeft: `4px solid ${colors.border}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', animation: 'toastIn 0.2s ease',
          }}>
            <span style={{ fontWeight: 700, color: colors.color, fontSize: 12 }}>{colors.icon}</span>
            <span style={{ flex: 1, fontSize: 12, color: '#1f2937', fontFamily: "'DM Sans', sans-serif" }}>{t.message}</span>
            <button onClick={() => removeToast(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
          </div>
        );
      })}
    </div>
  );
}

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes toastIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; height: 0; }
`;
